import {
  Box,
  Fab,
  Grid,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import TooltipCustomize from 'src/components/TooltipCustomize';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useQueryUrl from 'src/hooks/useQuery';
import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CUSTOM_REPORT, CUSTOM_REPORT_NEWSLATTER } from 'src/route';
import ConfirmationWithDiscard from 'src/components/ConfirmationWithDiscard';
import { useAppDispatch } from 'src/app/hooks';
import {
  initialSelectedNewsLetter,
  reducerResetSelectedNewsLetter,
  reducerUpdateActiveSection
} from 'src/features/customNewsLetters';
import { TCreateNewsletterType } from 'src/content/main/CustomReport/Newsletter/NewsletterById';
import useActionNewsLetter from 'src/content/main/CustomReport/Newsletter/hooks/useActionNewsLetter';

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    z-index: 5;
    box-shadow: ${theme.header.boxShadow};
    justify-content: space-between;
    width: 100%;
    background: ${theme.header.background};
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

export const stepsNewsLatter = [
  'Customize Template',
  'Select Source',
  'Email Settings'
];

const scrollToSection = (targetRef: React.RefObject<HTMLDivElement>) => {
  const element = targetRef.current;
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

function HeaderNewsLetterById() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const query = useQueryUrl();
  const actionQuery = query.get('action');
  const stepQuery = query.get('step');
  const newsLetterId = query.get('id');
  const typeQuery = query.get('type') as TCreateNewsletterType;
  const newsletterByIdRef = React.useRef<HTMLDivElement>(null);

  const [openConfirmContinouEditing, setConfirmContinouEditing] =
    React.useState<boolean>(false);

  const { handleResetPayload } = useActionNewsLetter();

  const activeStep = React.useMemo(() => {
    return stepQuery ? Number(stepQuery) - 1 : 0;
  }, [stepQuery]);

  const handleConfirContinueEdit = () => {
    setConfirmContinouEditing(false);
  };

  /**Handle confirm change */
  const handleClickCancel = () => {
    setConfirmContinouEditing(true);
  };

  const handleDiscard = () => {
    handleResetPayload();
    setConfirmContinouEditing(false);
    navigate(`${CUSTOM_REPORT}?tab=news_letter`);
  };

  /** handle step */
  const handleStep = (step: number) => () => {
    // auto select section when click step 2 (select source)
    if (step === 1) {
      dispatch(reducerUpdateActiveSection('print_media'));
    } else {
      dispatch(reducerUpdateActiveSection(''));
    }
    navigate(
      `${CUSTOM_REPORT_NEWSLATTER}?${
        newsLetterId ? `id=${newsLetterId}&` : ''
      }action=${actionQuery}&type=${typeQuery}&step=${step + 1}`
    );
  };

  React.useEffect(() => {
    console.log(typeQuery, 'type query');

    if (!actionQuery || !typeQuery || typeQuery == null) {
      navigate(`${CUSTOM_REPORT}?tab=news_letter`);
    }
  }, [actionQuery, typeQuery]);

  React.useEffect(() => {
    scrollToSection(newsletterByIdRef);
  }, [stepQuery]);

  return (
    <HeaderWrapper ref={newsletterByIdRef} display="flex" alignItems="center">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Stack direction="row" alignItems="center">
              <Fab
                sx={{
                  background: theme.palette.common.white,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  mr: theme.spacing(2)
                }}
                size="small"
                onClick={handleClickCancel}
              >
                <ArrowBackIcon />
              </Fab>
              <Typography variant="h1">
                {actionQuery === 'edit'
                  ? 'Edit Newsletter'
                  : 'Create Newsletter'}
              </Typography>
            </Stack>
            <TooltipCustomize
              title={'Build Custom Report'}
              placement="top-start"
            >
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ transform: 'translateY(-4px)' }}
              />
            </TooltipCustomize>
          </Stack>
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="center">
          <Box width={{ xs: '100%', md: '50%' }}>
            <Stepper
              nonLinear
              activeStep={activeStep}
              sx={{ background: 'none' }}
            >
              {stepsNewsLatter.map((label, index) => (
                <Step key={label}>
                  <StepButton color="inherit" onClick={handleStep(index)}>
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Grid>
      </Grid>
      <ConfirmationWithDiscard
        onClose={() => setConfirmContinouEditing(false)}
        onDiscard={handleDiscard}
        onOk={handleConfirContinueEdit}
        open={openConfirmContinouEditing}
        labelButton="Continue Editing"
        title="Are you sure?"
        message="You have unsaved changes. Do you want to discard them?"
        loading={false}
      />
    </HeaderWrapper>
  );
}

export default HeaderNewsLetterById;
